import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Employer, ScreeningUser } from 'screening/types';
import screeningClient, { ApiError } from 'screening/apiClient';
import { updateStoredEmployer } from '@hooks/useScreeningAuth';
import { useMapOverview } from 'screening/queries';
import Button from '../primitives/Button';
import { initialsOf } from '../primitives/BrowserBrandHeader';

interface AccountMenuProps {
  employer: Employer;
  user: ScreeningUser;
  onSignOut: () => void;
  /** Avatar-only trigger for narrow chrome. */
  compact?: boolean;
}

const Wrap = styled.div`
  position: relative;
`;

const Trigger = styled.button<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid var(--screening-hairline);
  background: var(--screening-paper);
  border-radius: var(--screening-r-input);
  padding: ${({ $compact }) => ($compact ? '4px' : '5px 10px 5px 5px')};
  cursor: pointer;
  font-family: var(--screening-font-ui);
  transition: border-color 160ms ease;

  &:hover {
    border-color: #cdd4da;
  }
`;

const Avatar = styled.span`
  width: 28px;
  height: 28px;
  border-radius: var(--screening-r-chip);
  background: var(--screening-navy);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  font-family: var(--screening-font-display);
`;

const TriggerName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--screening-ink);

  @media (max-width: 640px) {
    display: none;
  }
`;

const Panel = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  width: min(300px, calc(100vw - 32px));
  background: var(--screening-paper);
  border: 1px solid var(--screening-hairline);
  border-radius: var(--screening-r-card);
  box-shadow: var(--screening-shadow-overlay);
  padding: 16px;
  z-index: 100;
`;

const Company = styled.p`
  margin: 0;
  font-family: var(--screening-font-display);
  font-size: 15px;
  font-weight: 700;
  color: var(--screening-ink);
`;

const Email = styled.p`
  margin: 2px 0 14px;
  font-size: 13px;
  color: var(--screening-muted);
`;

const Divider = styled.div`
  height: 1px;
  background: var(--screening-hairline);
  margin: 12px 0;
`;

const VerifyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const VerifyLabel = styled.span`
  font-size: 13px;
  color: var(--screening-muted);
`;

const LinkButton = styled.button`
  border: none;
  background: none;
  color: var(--screening-navy);
  font-family: var(--screening-font-ui);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  &:hover {
    text-decoration: underline;
  }
`;

const CodeForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
`;

const CodeInput = styled.input`
  font-family: var(--screening-font-ui);
  font-size: 15px;
  letter-spacing: 0.3em;
  color: var(--screening-ink);
  border: 1px solid var(--screening-hairline);
  border-radius: var(--screening-r-input);
  padding: 8px 12px;
  &:focus {
    outline: none;
    border-color: var(--screening-navy);
  }
`;

const Verified = styled.span`
  font-size: 13px;
  color: var(--screening-advance);
  font-weight: 600;
`;

const SignOut = styled.button`
  border: none;
  background: none;
  color: var(--screening-muted);
  font-family: var(--screening-font-ui);
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  &:hover {
    color: var(--screening-ink);
  }
`;

const MenuLink = styled.a`
  display: block;
  margin-bottom: 10px;
  color: var(--screening-ink);
  font-family: var(--screening-font-ui);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const SeatButton = styled.button`
  display: block;
  width: 100%;
  margin-top: 8px;
  padding: 0;
  border: 0;
  background: none;
  text-align: left;
  color: var(--screening-ink);
  font-family: var(--screening-font-ui);
  font-size: 14px;
  cursor: pointer;

  small {
    display: block;
    color: var(--screening-muted);
    font-size: 12px;
  }
`;

const AccountMenu = ({
  employer,
  user,
  onSignOut,
  compact = false,
}: AccountMenuProps) => {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [wantSeats, setWantSeats] = useState(false);
  const overview = useMapOverview(wantSeats || open);
  const [showCode, setShowCode] = useState(false);
  const [code, setCode] = useState('');
  const [verified, setVerified] = useState(employer.email_verified);
  const [submitting, setSubmitting] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || code.length < 6) return;
    setSubmitting(true);
    try {
      const res = await screeningClient.verifyEmail({
        email: user.email,
        code,
      });
      if (res.email_verified) {
        setVerified(true);
        setShowCode(false);
        updateStoredEmployer({ ...employer, email_verified: true });
        toast.success('Email verified');
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'That code did not work'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Wrap ref={ref}>
      <Trigger
        type="button"
        $compact={compact}
        aria-label={employer.company_name}
        onMouseEnter={() => setWantSeats(true)}
        onFocus={() => setWantSeats(true)}
        onClick={() => setOpen((v) => !v)}
      >
        <Avatar>{initialsOf(employer.company_name)}</Avatar>
        {!compact && <TriggerName>{employer.company_name}</TriggerName>}
      </Trigger>
      {open && (
        <Panel>
          <Company>{employer.company_name}</Company>
          <Email>{user.email}</Email>
          <Divider />
          <VerifyRow>
            <VerifyLabel>Email</VerifyLabel>
            {verified ? (
              <Verified>Verified</Verified>
            ) : (
              <LinkButton onClick={() => setShowCode((v) => !v)}>
                {showCode ? 'Cancel' : 'Verify email'}
              </LinkButton>
            )}
          </VerifyRow>
          {!verified && showCode && (
            <CodeForm onSubmit={verify}>
              <CodeInput
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                placeholder="Enter 6-digit code"
                inputMode="numeric"
                aria-label="Verification code"
              />
              <Button
                type="submit"
                size="sm"
                disabled={submitting || code.length < 6}
              >
                {submitting ? 'Checking' : 'Confirm code'}
              </Button>
            </CodeForm>
          )}
          {(overview.data?.seats || []).length > 0 && (
            <>
              <Divider />
              <VerifyLabel>Seat</VerifyLabel>
              {overview.data?.seats.map((seat) => (
                <SeatButton
                  key={seat.id}
                  type="button"
                  onClick={() => {
                    const next =
                      seat.kind === 'company_admin'
                        ? { kind: 'company_admin' as const }
                        : {
                            kind: 'team_lead' as const,
                            team_id: seat.team_id || '',
                          };
                    window.dispatchEvent(
                      new CustomEvent('screening-map-seat', { detail: next })
                    );
                    // The map is held back until it is ready to ship.
                    // router.push(
                    //   seat.kind === 'company_admin' || !seat.team_id
                    //     ? '/screening/maps'
                    //     : `/screening/maps/teams/${seat.team_id}`
                    // );
                    router.push('/screening');
                    setOpen(false);
                  }}
                >
                  {seat.label}
                  {seat.title ? <small>{seat.title}</small> : null}
                </SeatButton>
              ))}
            </>
          )}
          <Divider />
          <Link href="/screening/settings" passHref legacyBehavior>
            <MenuLink onClick={() => setOpen(false)}>Settings</MenuLink>
          </Link>
          <SignOut onClick={onSignOut}>Sign out</SignOut>
        </Panel>
      )}
    </Wrap>
  );
};

export default AccountMenu;
